/**
 * Onde cada papel VIVE — a fronteira de acesso do portal, do lado do browser.
 *
 * A spec do portal (docs/superpowers/specs/2026-08-05-portal-do-cliente-design.md,
 * §4) desenha a fronteira no servidor: `requireAuth` recusa `role='customer'`
 * fora de `/api/portal`, e `requirePortalUser` recusa a Genesis dentro dele.
 * Este módulo é o ESPELHO de interface dessa regra — e só isso.
 *
 * ⚠️ Ele NÃO é uma garantia de segurança. Um redirect de cliente é UX: impede
 * que alguém aterre numa tela que não é sua e veja um esqueleto a carregar
 * seguido de erros. Quem protege o dado é a API — nenhuma tela interna passa a
 * ser segura por causa deste ficheiro, e nenhuma deixa de o ser se ele falhar.
 * É a mesma postura que o `admin-route.tsx` já declara sobre o `requireAdmin`.
 *
 * Vive em `lib/` e não num componente de rota porque três sítios precisam da
 * MESMA resposta e nenhum deles deve ter uma opinião própria sobre ela:
 *
 *   • `hooks/use-auth.ts`      — para onde se entra depois do sign-in;
 *   • `routes/sign-in.tsx`     — para onde vai quem já tem sessão e reabre /sign-in;
 *   • `routes/protected-route.tsx` / `routes/portal-route.tsx` — as duas guardas.
 *
 * Duas moradas para "onde é a casa deste papel" é como um dos caminhos fica
 * para trás quando um papel novo aparecer.
 */
import type { UserRole } from '@/lib/api/me'

/** A raiz do portal do cliente. Tudo o que o cliente pode ver pende daqui. */
export const PORTAL_HOME = '/portal'

/** A casa de quem é da Genesis (admin e recruiter). */
export const GENESIS_HOME = '/dashboard'

/**
 * O destino de quem acabou de entrar, ou de quem aterrou onde não devia.
 *
 * Papel desconhecido (`undefined`/`null`) cai na casa da Genesis de propósito:
 * é o comportamento que o produto tinha antes do portal existir, e a guarda de
 * `/portal` recusa quem lá não deve estar de qualquer forma. O caso acontece de
 * verdade — entre o POST do sign-in e a leitura de `/api/me` há um instante em
 * que a sessão existe e o papel ainda não foi lido.
 */
export function homeForRole(role: UserRole | null | undefined): string {
  return role === 'customer' ? PORTAL_HOME : GENESIS_HOME
}
